/**
 * 宏事件类型与各类型操作值（Byte1）的编解码。
 *
 * 普通宏事件（类型 1-7）为 4 字节：
 *   Byte0: bit7=动作(1抬起,0按下), 低7位=类型
 *   Byte1: 操作值
 *   Byte2-3: 延时（小端序, ms）
 *
 * Byte1 的含义随类型变化：
 *   类型 1/2/3（键盘 / 修饰键 / 多媒体）: HID 键码，无符号
 *   类型 4（鼠标按键）: 位掩码，bit0 左键 bit1 右键 bit2 中键 bit3 侧后 bit4 侧前
 *   类型 5/6/7（滚轮 / 鼠标X / 鼠标Y）: 有符号字节，-127 ~ 127
 *
 * 鼠标移动事件（类型 8）同样占 4 字节，但 Byte1-3 全部用于坐标，没有延时字段：
 *   Byte0: 类型 8
 *   Byte1: X 低 8 位
 *   Byte2: Y 低 8 位
 *   Byte3: Bit0-Bit4 = X 高 5 位, Bit5-Bit7 = Y 高 3 位
 * 即 X 为 13 位有符号数(-4096~4095)，Y 为 11 位有符号数(-1024~1023)，
 * 设备按固定 5ms 延时执行该事件。
 */

export const MACRO_EVENT_TYPE_KEYBOARD = 1;
export const MACRO_EVENT_TYPE_MODIFIER = 2;
export const MACRO_EVENT_TYPE_MEDIA = 3;
export const MACRO_EVENT_TYPE_MOUSE_BUTTON = 4;
export const MACRO_EVENT_TYPE_WHEEL = 5;
export const MACRO_EVENT_TYPE_MOUSE_X = 6;
export const MACRO_EVENT_TYPE_MOUSE_Y = 7;
export const MACRO_EVENT_TYPE_MOUSE_MOVE = 8;

export const MACRO_EVENT_TYPE_NAMES = {
  [MACRO_EVENT_TYPE_KEYBOARD]: '键盘',
  [MACRO_EVENT_TYPE_MODIFIER]: '修饰键',
  [MACRO_EVENT_TYPE_MEDIA]: '多媒体',
  [MACRO_EVENT_TYPE_MOUSE_BUTTON]: '鼠标按键',
  [MACRO_EVENT_TYPE_WHEEL]: '滚轮',
  [MACRO_EVENT_TYPE_MOUSE_X]: '鼠标X',
  [MACRO_EVENT_TYPE_MOUSE_Y]: '鼠标Y',
  [MACRO_EVENT_TYPE_MOUSE_MOVE]: '鼠标移动',
};

// 鼠标移动事件不带延时字段，设备固定按 5ms 执行。
export const MACRO_MOUSE_MOVE_DELAY_MS = 5;

export const MACRO_MOUSE_MOVE_X_MIN = -4096;
export const MACRO_MOUSE_MOVE_X_MAX = 4095;
export const MACRO_MOUSE_MOVE_Y_MIN = -1024;
export const MACRO_MOUSE_MOVE_Y_MAX = 1023;

const clamp = (value, min, max) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(min, Math.min(max, Math.trunc(number)));
};

export const clampMacroMoveX = (value) =>
  clamp(value, MACRO_MOUSE_MOVE_X_MIN, MACRO_MOUSE_MOVE_X_MAX);

export const clampMacroMoveY = (value) =>
  clamp(value, MACRO_MOUSE_MOVE_Y_MIN, MACRO_MOUSE_MOVE_Y_MAX);

/**
 * 将 X/Y 位移编码为事件的 Byte1-Byte3。
 * @param {number} x - -4096 ~ 4095
 * @param {number} y - -1024 ~ 1023
 * @returns {number[]} [byte1, byte2, byte3]
 */
export function encodeMacroMouseMove(x, y) {
  const x13 = clampMacroMoveX(x) & 0x1fff;  // 13 位
  const y11 = clampMacroMoveY(y) & 0x7ff;   // 11 位

  return [
    x13 & 0xff,                                              // Byte1: X 低 8 位
    y11 & 0xff,                                              // Byte2: Y 低 8 位
    ((x13 >> 8) & 0x1f) | (((y11 >> 8) & 0x07) << 5),        // Byte3: X 高 5 位 + Y 高 3 位
  ];
}

/**
 * 将事件的 Byte1-Byte3 解码为 X/Y 位移。
 * @returns {{x: number, y: number}}
 */
export function decodeMacroMouseMove(byte1, byte2, byte3) {
  const rawX = ((byte1 || 0) & 0xff) | (((byte3 || 0) & 0x1f) << 8);
  const rawY = ((byte2 || 0) & 0xff) | ((((byte3 || 0) >> 5) & 0x07) << 8);

  return {
    x: rawX & 0x1000 ? rawX - 0x2000 : rawX,  // 13 位有符号
    y: rawY & 0x400 ? rawY - 0x800 : rawY,    // 11 位有符号
  };
}

/**
 * 鼠标移动事件自协议 V3.0 起支持。
 * 版本未知（未连接设备、仅编辑本地宏）时按支持处理，避免本地编辑被误锁。
 * @param {string|null|undefined} protocolVersion - 例如 V3.0、V3.1.2
 */
export function supportsMacroMouseMove(protocolVersion) {
  if (!protocolVersion) return true;
  const match = String(protocolVersion).match(/(\d+)(?:\.(\d+))?(?:\.(\d+))?/);
  if (!match) return true;
  return Number(match[1]) >= 3;
}

export const formatMacroMoveLabel = (x, y) =>
  `移动 X:${clampMacroMoveX(x)} Y:${clampMacroMoveY(y)}`;

/* ---------- 宏循环模式 ---------- */

/**
 * 循环模式的类型码，两条按键绑定协议共用同一套取值，只是存放位置不同：
 *   宏9  (0x09)：按键绑定键值 0x09[次数][序号][类型]，宏对象 byte5/byte2-3 恒为 1
 *   宏12 (0x0C)：宏对象 byte5=类型、byte2-3=次数，按键绑定为 0x0C01[序号]00
 * 4.0 起新绑定写宏12；读回时以绑定类型为准，不单看协议版本。
 */
export const MACRO_LOOP_TYPE_ONCE = 1;           // 只发一次，停止
export const MACRO_LOOP_TYPE_UNTIL_RELEASE = 2;  // 按住按键一直发，放开停止
export const MACRO_LOOP_TYPE_UNTIL_ANY_KEY = 3;  // 循环发送，直到任意按键按下
export const MACRO_LOOP_TYPE_COUNT = 4;          // 连发 N 次，到发送完成后停止
export const MACRO_LOOP_TYPE_TOGGLE = 5;         // 循环发送，直到按键再次按下
export const MACRO_LOOP_TYPE_BURST_ANY_KEY = 6;  // 连发 N 次，直到任意按键按下（仅 4.0）

// 次数字段：宏9 是绑定里的单字节，宏12 是宏对象的 uint16。
// 上限统一取两者的交集，避免同一份宏在两条协议间搬运时被截断。
export const MACRO_LOOP_COUNT_MIN = 1;
export const MACRO_LOOP_COUNT_MAX = 255;

const MACRO_LOOP_TYPE_TO_MODE = {
  [MACRO_LOOP_TYPE_ONCE]: 'once',
  [MACRO_LOOP_TYPE_UNTIL_RELEASE]: 'release',
  [MACRO_LOOP_TYPE_UNTIL_ANY_KEY]: 'anyKey',
  [MACRO_LOOP_TYPE_COUNT]: 'count',
  [MACRO_LOOP_TYPE_TOGGLE]: 'toggle',
  [MACRO_LOOP_TYPE_BURST_ANY_KEY]: 'burstAnyKey',
};

const MACRO_LOOP_MODE_TO_TYPE = {
  once: MACRO_LOOP_TYPE_ONCE,
  release: MACRO_LOOP_TYPE_UNTIL_RELEASE,
  anyKey: MACRO_LOOP_TYPE_UNTIL_ANY_KEY,
  count: MACRO_LOOP_TYPE_COUNT,
  toggle: MACRO_LOOP_TYPE_TOGGLE,
  burstAnyKey: MACRO_LOOP_TYPE_BURST_ANY_KEY,
};

export const macroLoopModeFromType = (type) =>
  MACRO_LOOP_TYPE_TO_MODE[Number(type)] || 'once';

export const macroLoopTypeFromMode = (mode) =>
  MACRO_LOOP_MODE_TO_TYPE[mode] || MACRO_LOOP_TYPE_ONCE;

/** 类型 4 / 6 的次数字段有效。类型 6 仅 4.0 宏12 使用。 */
export const macroLoopTypeUsesCount = (type) => {
  const value = Number(type);
  return value === MACRO_LOOP_TYPE_COUNT || value === MACRO_LOOP_TYPE_BURST_ANY_KEY;
};

export const macroLoopModeUsesCount = (mode) =>
  macroLoopTypeUsesCount(macroLoopTypeFromMode(mode));

export const clampMacroLoopCount = (value) => {
  const number = Math.trunc(Number(value));
  if (!Number.isFinite(number)) return MACRO_LOOP_COUNT_MIN;
  return clamp(number, MACRO_LOOP_COUNT_MIN, MACRO_LOOP_COUNT_MAX);
};

/**
 * 协议 4.0 起新绑定改用宏12，循环模式改由宏对象承载。
 * 版本未知时按宏9 处理：宁可循环设置落在绑定上，也不要写坏旧协议键值。
 * @param {string|null|undefined} protocolVersion - 例如 V4.0、V3.1.2
 */
export function macroLoopLivesInMacroObject(protocolVersion) {
  if (!protocolVersion) return false;
  const match = String(protocolVersion).match(/(\d+)(?:\.(\d+))?(?:\.(\d+))?/);
  if (!match) return false;
  return Number(match[1]) >= 4;
}

/* ---------- 按键绑定：宏9 / 宏12 ---------- */

/** 旧协议：循环写在按键绑定里 */
export const MACRO_BINDING_TYPE_LEGACY = 0x09;
/** 新协议：循环写在宏数据里 */
export const MACRO_BINDING_TYPE_IN_MACRO = 0x0C;

const hexByte = (value) =>
  (Number(value) & 0xff).toString(16).padStart(2, '0').toUpperCase();

const normalizeBindingHex = (value) => {
  if (typeof value !== 'string') return '';
  const hex = value.replace(/^0x/i, '').toUpperCase();
  return hex.length === 8 ? hex : '';
};

/**
 * 解析按键绑定是否为宏，以及循环写在绑定还是宏对象里。
 * 宏9：0x09[次数][序号][类型]
 * 宏12：0x0C01[序号]00
 * @returns {{
 *   bindingType: number,
 *   macroIndex: number,
 *   loopType: number|null,
 *   loopCount: number|null,
 *   loopInMacroObject: boolean,
 *   rawValue: string,
 * }|null}
 */
export function parseMacroBindingValue(value) {
  const hex = normalizeBindingHex(value);
  if (!hex) return null;

  const bindingType = parseInt(hex.substring(0, 2), 16);
  if (
    bindingType !== MACRO_BINDING_TYPE_LEGACY &&
    bindingType !== MACRO_BINDING_TYPE_IN_MACRO
  ) {
    return null;
  }

  const macroIndex = parseInt(hex.substring(4, 6), 16);
  const loopInMacroObject = bindingType === MACRO_BINDING_TYPE_IN_MACRO;

  if (loopInMacroObject) {
    return {
      bindingType,
      macroIndex,
      loopType: null,
      loopCount: null,
      loopInMacroObject: true,
      rawValue: `0x${hex}`,
    };
  }

  const loopType = parseInt(hex.substring(6, 8), 16);
  const loopCount = macroLoopTypeUsesCount(loopType)
    ? clampMacroLoopCount(parseInt(hex.substring(2, 4), 16))
    : MACRO_LOOP_COUNT_MIN;

  return {
    bindingType,
    macroIndex,
    loopType,
    loopCount,
    loopInMacroObject: false,
    rawValue: `0x${hex}`,
  };
}

export const isMacroBindingValue = (value) =>
  parseMacroBindingValue(value) !== null;

export const macroBindingTypeForProtocol = (protocolVersion) =>
  macroLoopLivesInMacroObject(protocolVersion)
    ? MACRO_BINDING_TYPE_IN_MACRO
    : MACRO_BINDING_TYPE_LEGACY;

/**
 * 编码宏按键绑定。
 * 宏12 不编码循环，格式为 0x0C01[序号]00；宏9 把次数/类型写进绑定。
 */
export function encodeMacroBindingValue({
  bindingType,
  macroIndex,
  loopMode = 'once',
  loopCount = MACRO_LOOP_COUNT_MIN,
} = {}) {
  const type =
    Number(bindingType) === MACRO_BINDING_TYPE_IN_MACRO
      ? MACRO_BINDING_TYPE_IN_MACRO
      : MACRO_BINDING_TYPE_LEGACY;
  const indexHex = hexByte(macroIndex);

  if (type === MACRO_BINDING_TYPE_IN_MACRO) {
    return `0x${hexByte(MACRO_BINDING_TYPE_IN_MACRO)}01${indexHex}00`;
  }

  const loopType = macroLoopTypeFromMode(loopMode);
  // 宏9 没有类型6；连发直到任意键只写在 4.0 宏对象里。
  if (loopType === MACRO_LOOP_TYPE_COUNT) {
    return `0x${hexByte(MACRO_BINDING_TYPE_LEGACY)}${hexByte(
      clampMacroLoopCount(loopCount)
    )}${indexHex}${hexByte(MACRO_LOOP_TYPE_COUNT)}`;
  }

  return `0x${hexByte(MACRO_BINDING_TYPE_LEGACY)}01${indexHex}${hexByte(loopType)}`;
}

export function encodeNewMacroBinding(protocolVersion, macroIndex) {
  return encodeMacroBindingValue({
    bindingType: macroBindingTypeForProtocol(protocolVersion),
    macroIndex,
    loopMode: 'once',
    loopCount: MACRO_LOOP_COUNT_MIN,
  });
}

/* ---------- 类型 4：鼠标按键（Byte1 为位掩码） ---------- */

export const MACRO_MOUSE_BUTTON_LEFT = 0x01;     // bit0
export const MACRO_MOUSE_BUTTON_RIGHT = 0x02;    // bit1
export const MACRO_MOUSE_BUTTON_MIDDLE = 0x04;   // bit2
export const MACRO_MOUSE_BUTTON_BACK = 0x08;     // bit3 侧后键
export const MACRO_MOUSE_BUTTON_FORWARD = 0x10;  // bit4 侧前键

export const MACRO_MOUSE_BUTTON_MASK = 0x1f;

export const MACRO_MOUSE_BUTTON_OPTIONS = [
  { value: MACRO_MOUSE_BUTTON_LEFT, label: '左键' },
  { value: MACRO_MOUSE_BUTTON_RIGHT, label: '右键' },
  { value: MACRO_MOUSE_BUTTON_MIDDLE, label: '中键' },
  { value: MACRO_MOUSE_BUTTON_BACK, label: '侧后键' },
  { value: MACRO_MOUSE_BUTTON_FORWARD, label: '侧前键' },
];

export const clampMacroMouseButtons = (mask) => {
  const number = Number(mask);
  if (!Number.isFinite(number)) return 0;
  return Math.trunc(number) & MACRO_MOUSE_BUTTON_MASK;
};

/**
 * 位掩码转显示名。多个位同时置位时用 + 连接（设备允许一个事件按下多个键）。
 */
export function formatMacroMouseButtons(mask) {
  const bits = clampMacroMouseButtons(mask);
  if (!bits) return '未设置按键';

  return MACRO_MOUSE_BUTTON_OPTIONS
    .filter((option) => (bits & option.value) !== 0)
    .map((option) => option.label)
    .join('+');
}

/* ---------- 类型 5/6/7：滚轮 / 鼠标X / 鼠标Y（Byte1 为有符号字节） ---------- */

export const MACRO_AXIS_MIN = -127;
export const MACRO_AXIS_MAX = 127;

export const MACRO_AXIS_EVENT_TYPES = [
  MACRO_EVENT_TYPE_WHEEL,
  MACRO_EVENT_TYPE_MOUSE_X,
  MACRO_EVENT_TYPE_MOUSE_Y,
];

export const isMacroAxisEventType = (type) =>
  MACRO_AXIS_EVENT_TYPES.includes(Number(type));

export const clampMacroAxisValue = (value) =>
  clamp(value, MACRO_AXIS_MIN, MACRO_AXIS_MAX);

/** 有符号数值 -> Byte1（补码） */
export const encodeMacroAxisValue = (value) => clampMacroAxisValue(value) & 0xff;

/** Byte1（补码）-> 有符号数值 */
export const decodeMacroAxisValue = (byte) => {
  const raw = (Number(byte) || 0) & 0xff;
  return raw > 127 ? raw - 256 : raw;
};

export const formatMacroAxisLabel = (eventType, value) => {
  const name = MACRO_EVENT_TYPE_NAMES[eventType] || `类型${eventType}`;
  const amount = clampMacroAxisValue(value);
  return `${name} ${amount > 0 ? '+' : ''}${amount}`;
};
